/**
 * Layout of the preview stage (W2-02; DESIGN.md §4.8 "Screen", rendering.md R§1.2).
 *
 * The stage is a container holding two canvases:
 *  - the GL canvas IS the simulated device screen: its size is the largest rectangle of the screen shape's aspect
 *    that fits the container (or the whole container for "fill"), magnified by the Edit-mode view zoom and moved by
 *    the view pan. Phira's own letterboxing then happens inside it (engine viewport, R§1.2);
 *  - the overlay canvas covers the whole container, so when the view is zoomed out, outlines and labels of lines
 *    outside the screen are still drawn around it.
 * All values are CSS pixels unless stated; device pixels are CSS pixels times the canvas pixel ratio.
 */

import type { Viewport } from '../../core/engine/types';

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/** Edit-mode view transform of the stage (zoom about the container centre, then pan in CSS px). */
export interface StageView {
  readonly zoom: number;
  readonly panX: number;
  readonly panY: number;
}

export const IDENTITY_VIEW: StageView = Object.freeze({ zoom: 1, panX: 0, panY: 0 });

export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 6;
/** The GL canvas never gets more device pixels than this (zoomed-in views lower its pixel ratio). */
export const MAX_GL_PIXELS = 3840 * 2160 * 1.25;

export function clampZoom(z: number): number {
  if (!Number.isFinite(z)) return 1;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/** Largest rectangle of aspect `aspect` (w/h) centred in a cw x ch box. */
export function fitAspect(cw: number, ch: number, aspect: number): Rect {
  if (!(cw > 0) || !(ch > 0) || !(aspect > 0)) return { x: 0, y: 0, w: Math.max(0, cw), h: Math.max(0, ch) };
  let w = cw;
  let h = cw / aspect;
  if (h > ch) {
    h = ch;
    w = ch * aspect;
  }
  return { x: (cw - w) / 2, y: (ch - h) / 2, w, h };
}

/**
 * The GL canvas rectangle inside the container: the screen of `aspect` (null = the container itself) fitted,
 * zoomed about the container centre and panned. Sizes are rounded to whole CSS pixels.
 */
export function screenRect(cw: number, ch: number, aspect: number | null, view: StageView): Rect {
  const base = aspect === null ? { x: 0, y: 0, w: Math.max(0, cw), h: Math.max(0, ch) } : fitAspect(cw, ch, aspect);
  const z = clampZoom(view.zoom);
  const w = Math.max(1, Math.round(base.w * z));
  const h = Math.max(1, Math.round(base.h * z));
  const x = Math.round(cw / 2 + (base.x + base.w / 2 - cw / 2) * z - w / 2 + view.panX);
  const y = Math.round(ch / 2 + (base.y + base.h / 2 - ch / 2) * z - h / 2 + view.panY);
  return { x, y, w, h };
}

/** Pixel ratio of the GL canvas: the display ratio, lowered so the canvas stays under MAX_GL_PIXELS. */
export function glPixelRatio(dpr: number, w: number, h: number, maxPixels: number = MAX_GL_PIXELS): number {
  const r = dpr > 0 ? dpr : 1;
  const area = w * h * r * r;
  if (!(area > maxPixels) || area === 0) return r;
  return Math.max(0.25, r * Math.sqrt(maxPixels / area));
}

/** Device-pixel size of a canvas of `w` x `h` CSS px at ratio `r` (at least 1 x 1). */
export function backingSize(w: number, h: number, r: number): readonly [number, number] {
  return [Math.max(1, Math.round(w * r)), Math.max(1, Math.round(h * r))];
}

/**
 * The Phira viewport of the GL canvas (in its device px) expressed in the overlay canvas' device px: the overlay
 * covers the container at ratio `overlayDpr`, the GL canvas sits at `screen` (CSS px) with ratio `glDpr`.
 */
export function overlayViewport(
  gl: Viewport,
  screen: Rect,
  glDpr: number,
  overlayDpr: number,
  overlayW: number,
  overlayH: number,
): Viewport {
  const k = overlayDpr / (glDpr > 0 ? glDpr : 1);
  return {
    winW: overlayW,
    winH: overlayH,
    vx: screen.x * overlayDpr + gl.vx * k,
    vy: screen.y * overlayDpr + gl.vy * k,
    W: gl.W * k,
    H: gl.H * k,
    ar: gl.ar,
  };
}

/** Zoom by `factor` keeping the container point (ax, ay) (CSS px) fixed on screen. */
export function zoomAt(view: StageView, factor: number, ax: number, ay: number, cw: number, ch: number): StageView {
  const zoom = clampZoom(view.zoom * factor);
  const k = zoom / view.zoom;
  // A point p maps to c + (p0 - c) * z + pan; keep it fixed: pan' = (a - c) - (a - c - pan) * k.
  const dx = ax - cw / 2;
  const dy = ay - ch / 2;
  return { zoom, panX: dx - (dx - view.panX) * k, panY: dy - (dy - view.panY) * k };
}

/** The view that centres a point of the GL canvas (CSS px relative to the screen's top-left) in the container. */
export function centreOn(view: StageView, screen: Rect, px: number, py: number, cw: number, ch: number): StageView {
  return {
    zoom: view.zoom,
    panX: view.panX + (cw / 2 - (screen.x + px)),
    panY: view.panY + (ch / 2 - (screen.y + py)),
  };
}

/** True when the view is the identity (no zoom, no pan). */
export function isIdentityView(v: StageView): boolean {
  return Math.abs(v.zoom - 1) < 1e-9 && v.panX === 0 && v.panY === 0;
}
